import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { getSelectedCompanyId, listCompanies, setSelectedCompanyId } from "./api";
import { useAuth } from "./auth-context";
import type { ManagedCompany } from "../types/domain";

interface CompanyContextValue {
  /** Mandanti del workspace. Vuoto su un workspace che non e' uno studio. */
  companies: ManagedCompany[];
  /** Mandante selezionata, o null per "tutte le mandanti". */
  selectedId: string | null;
  selected: ManagedCompany | null;
  /** true se il workspace ha almeno una mandante: governa la UI dedicata. */
  isStudio: boolean;
  select: (companyId: string | null) => void;
  reload: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue>({
  companies: [],
  selectedId: null,
  selected: null,
  isStudio: false,
  select: () => undefined,
  reload: async () => undefined
});

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { tokens } = useAuth();
  const [companies, setCompanies] = useState<ManagedCompany[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(getSelectedCompanyId());

  const reload = useCallback(async () => {
    if (!tokens?.access_token) {
      setCompanies([]);
      return;
    }
    try {
      setCompanies(await listCompanies(tokens.access_token));
    } catch {
      // Un workspace senza permessi o senza mandanti non e' un errore da mostrare.
      setCompanies([]);
    }
  }, [tokens?.access_token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Una mandante cancellata o di un altro workspace non deve restare selezionata:
  // il backend risponderebbe 404 su ogni chiamata.
  useEffect(() => {
    if (selectedId && companies.length > 0 && !companies.some((c) => c.id === selectedId)) {
      setSelectedCompanyId(null);
      setSelectedId(null);
    }
  }, [companies, selectedId]);

  const select = useCallback((companyId: string | null) => {
    setSelectedCompanyId(companyId);
    setSelectedId(companyId);
  }, []);

  const value = useMemo<CompanyContextValue>(
    () => ({
      companies,
      selectedId,
      selected: companies.find((c) => c.id === selectedId) ?? null,
      isStudio: companies.length > 0,
      select,
      reload
    }),
    [companies, selectedId, select, reload]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompanies(): CompanyContextValue {
  return useContext(CompanyContext);
}
